/**
 * CompanyDetailPage v2 — Sade, minimal, 3 bolum:
 *   1) Header + KPI (Net Bakiye + 2 ozet)
 *   2) Inbox (eslestirme akisi)
 *   3) Defter (kronolojik tum hareketler)
 *
 * Tum logik tek bir backend endpoint'inden: /accounting/companies/{id}/ledger
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';
import {
    ArrowLeft, FileDown, GitMerge, AlertTriangle, Inbox, BookOpen, RefreshCw, Link2,
} from 'lucide-react';
import LedgerInbox from './accounting/LedgerInbox';
import LedgerView from './accounting/LedgerView';
import MatchedTransactions from './accounting/MatchedTransactions';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CompanyDetailPage() {
    const { companyId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [tab, setTab] = useState('inbox'); // 'inbox' | 'ledger' | 'matched'
    const refetchTimer = useRef(null);

    const fetchData = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setRefreshing(true);
        try {
            const r = await axios.get(`${API}/api/accounting/companies/${companyId}/ledger`, { headers: headers() });
            setData(r.data);
        } catch (e) {
            if (e.response?.status === 404) { toast.error('Firma bulunamadi'); navigate('/accounting'); }
            else if (!silent) toast.error('Veri yuklenemedi');
        } finally { setLoading(false); setRefreshing(false); }
    }, [companyId, navigate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Optimistic update yardimcisi — children'dan cagrilir, anlik UI guncellemesi.
    // Ayrica debounce'lu silent background refresh tetikler (KPI/health sync icin).
    const applyLocalUpdate = useCallback((updater) => {
        setData(prev => prev ? updater(prev) : prev);
        // Debounced silent refetch — 800ms icinde birden fazla cagri gelirse tek seferde sync olur
        if (refetchTimer.current) clearTimeout(refetchTimer.current);
        refetchTimer.current = setTimeout(() => fetchData({ silent: true }), 800);
    }, [fetchData]);

    const handleDownloadPdf = async () => {
        try {
            const r = await axios.get(`${API}/api/accounting/companies/${companyId}/pdf`, {
                headers: headers(), responseType: 'blob',
            });
            const blob = new Blob([r.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = (data?.company?.name || 'company').replace(/[^a-z0-9-_ ]/gi, '') + '_CariOzet.pdf';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('PDF indirildi');
        } catch (e) {
            toast.error('PDF olusturulamadi');
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="p-12 text-center text-sm text-[#9CA3AF]" data-testid="acc-comp-page-loading">Yukleniyor...</div>
            </DashboardLayout>
        );
    }
    if (!data) {
        return (
            <DashboardLayout>
                <div className="p-12 text-center text-sm text-[#9CA3AF]">Veri yok</div>
            </DashboardLayout>
        );
    }

    const { company, kpis, counts, entries, inbox, health } = data;
    const matchedTx = data.matched_transactions || [];
    const netPos = kpis.net_balance >= 0; // pozitif -> karsi taraf bize borclu

    return (
        <DashboardLayout>
            <div className="space-y-4 max-w-[1400px] mx-auto" data-testid="acc-company-page">
                {/* HEADER */}
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/accounting?tab=companies')}
                        className="h-9 px-3 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#374151] text-sm font-medium rounded-xl flex items-center gap-1.5"
                        data-testid="acc-comp-back-btn">
                        <ArrowLeft size={14} /> Firmalar
                    </button>
                    <h1 className="text-xl font-bold text-[#111827] truncate flex-1">{company.name || '(isimsiz)'}</h1>
                    <button onClick={handleDownloadPdf}
                        className="h-9 px-3 bg-[#111] hover:bg-[#1F2937] text-white text-xs font-medium rounded-xl flex items-center gap-1.5"
                        data-testid="acc-comp-pdf-btn"
                        title="Cari hesap ozeti PDF olarak indir">
                        <FileDown size={12} /> PDF Indir
                    </button>
                    <button onClick={() => fetchData()} disabled={refreshing}
                        className="h-9 px-3 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#374151] text-xs font-medium rounded-xl flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="acc-comp-refresh-btn">
                        <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Yenileniyor...' : 'Yenile'}
                    </button>
                    <button onClick={async () => {
                        try {
                            const r = await axios.get(`${API}/api/accounting/reports/customer-statement/${companyId}.pdf`,
                                { headers: headers(), responseType: 'blob' });
                            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                            window.open(url, '_blank');
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch (e) { toast.error('PDF olusturulamadi'); }
                    }}
                        className="h-9 px-3 bg-white hover:bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-xl flex items-center gap-1.5"
                        data-testid="acc-comp-statement-pdf-btn">
                        <FileDown size={13} /> Hesap Ekstresi
                    </button>
                </div>

                {/* META */}
                <div className="bg-white rounded-2xl border border-[#E5E7EB] px-5 py-3">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#6B7280]">
                        {company.vat_number && <span><strong>KDV:</strong> {company.vat_number}</span>}
                        {company.iban && <span><strong>IBAN:</strong> {company.iban}</span>}
                        {company.email && <span><strong>E-posta:</strong> {company.email}</span>}
                        {company.phone && <span><strong>Tel:</strong> {company.phone}</span>}
                    </div>
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        {company.roles?.map(r => (
                            <span key={r} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${r === 'customer' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                                {r === 'customer' ? 'Musteri' : 'Tedarikci'}
                            </span>
                        ))}
                    </div>
                </div>

                {/* NET BAKIYE - HERO */}
                <div className={`rounded-2xl border-2 p-6 ${netPos ? 'bg-emerald-50 border-emerald-200' : Math.abs(kpis.net_balance) < 0.5 ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`}
                    data-testid="acc-comp-net-balance">
                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold mb-1">Net Cari Bakiye</p>
                            <p className={`text-4xl font-bold ${netPos ? 'text-emerald-700' : Math.abs(kpis.net_balance) < 0.5 ? 'text-[#374151]' : 'text-red-600'}`}>
                                {netPos ? '+' : ''}{fmt(kpis.net_balance)} EUR
                            </p>
                            <p className={`text-[12px] mt-1 font-semibold ${netPos ? 'text-emerald-700' : 'text-red-700'}`}>
                                {Math.abs(kpis.net_balance) < 0.5 ? '⚖ Hesap kapali' : netPos ? '↘ Bize borclu (alacakli durumdayiz)' : '↗ Biz borcluyuz'}
                            </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <MiniKpi label="Acik Alacak" value={kpis.open_receivable} color="emerald" testId="acc-comp-open-recv" />
                            <MiniKpi label="Acik Borc" value={kpis.open_payable} color="red" testId="acc-comp-open-pay" />
                        </div>
                    </div>
                </div>

                {/* OZET METRIKLER */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2">
                    <MetricCard label="Satis Fatura" value={kpis.sum_sales} count={counts.invoices} accent="emerald" />
                    <MetricCard label="Satis Iade" value={kpis.sum_sales_credit} count={counts.sales_credits} accent="rose" negative />
                    <MetricCard label="Tahsilat" value={kpis.bank_in} count={counts.payments_in} accent="teal" />
                    <MetricCard label="Alis Fatura" value={kpis.sum_purchase} count={counts.purchases} accent="blue" />
                    <MetricCard label="Alis Iade" value={kpis.sum_purchase_credit} count={counts.purchase_credits} accent="amber" />
                    <MetricCard label="Odeme" value={kpis.bank_out} count={counts.payments_out} accent="orange" />
                    <MetricCard label="Komisyon" value={kpis.bank_fees || 0} count={(kpis.bank_fees || 0) > 0 ? 'fee' : 0} accent="purple" testId="acc-comp-metric-bank-fees" />
                </div>

                {/* HEALTH UYARILARI */}
                {health && health.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="acc-comp-health">
                        <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle size={14} className="text-amber-600" />
                            <span className="text-[12px] font-bold text-amber-900">Veri Saglik Uyarisi</span>
                        </div>
                        <ul className="text-[11px] text-amber-800 space-y-0.5 ml-6">
                            {health.map((h, i) => <li key={i}>• {h.message}</li>)}
                        </ul>
                    </div>
                )}

                {/* TAB NAV */}
                <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
                    <div className="px-4 border-b border-[#F3F4F6] flex gap-1">
                        <button onClick={() => setTab('inbox')}
                            className={`px-4 py-3 text-[13px] font-semibold border-b-2 flex items-center gap-1.5 ${tab === 'inbox' ? 'border-[#111] text-[#111]' : 'border-transparent text-[#9CA3AF]'}`}
                            data-testid="acc-comp-page-tab-inbox">
                            <Inbox size={13} /> Inbox
                            {(counts.inbox_open_invoices + counts.inbox_unmatched_tx) > 0 && (
                                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500 text-white font-bold">
                                    {counts.inbox_open_invoices + counts.inbox_unmatched_tx}
                                </span>
                            )}
                        </button>
                        <button onClick={() => setTab('ledger')}
                            className={`px-4 py-3 text-[13px] font-semibold border-b-2 flex items-center gap-1.5 ${tab === 'ledger' ? 'border-[#111] text-[#111]' : 'border-transparent text-[#9CA3AF]'}`}
                            data-testid="acc-comp-page-tab-ledger">
                            <BookOpen size={13} /> Defter ({entries.length})
                        </button>
                        <button onClick={() => setTab('matched')}
                            className={`px-4 py-3 text-[13px] font-semibold border-b-2 flex items-center gap-1.5 ${tab === 'matched' ? 'border-[#111] text-[#111]' : 'border-transparent text-[#9CA3AF]'}`}
                            data-testid="acc-comp-page-tab-matched">
                            <Link2 size={13} /> Eslesmis ({counts.matched_tx || 0})
                        </button>
                    </div>
                    <div className="p-3">
                        {tab === 'inbox' ? (
                            <LedgerInbox inbox={inbox} onChange={fetchData} onLocalUpdate={applyLocalUpdate} />
                        ) : tab === 'matched' ? (
                            <MatchedTransactions items={matchedTx} onChange={fetchData} onLocalUpdate={applyLocalUpdate} />
                        ) : (
                            <LedgerView entries={entries} />
                        )}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

function MiniKpi({ label, value, color, testId }) {
    const cls = {
        emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        red: 'bg-red-100 text-red-800 border-red-200',
    }[color] || 'bg-gray-100 text-gray-800 border-gray-200';
    return (
        <div className={`rounded-xl border px-4 py-2 ${cls}`} data-testid={testId}>
            <p className="text-[10px] uppercase font-bold tracking-wider opacity-70">{label}</p>
            <p className="text-lg font-bold">{fmt(value)} EUR</p>
        </div>
    );
}

function MetricCard({ label, value, count, accent, negative, testId }) {
    const accentCls = {
        emerald: 'text-emerald-700',
        rose: 'text-rose-700',
        teal: 'text-teal-700',
        blue: 'text-blue-700',
        amber: 'text-amber-700',
        orange: 'text-orange-700',
        purple: 'text-purple-700',
    }[accent] || 'text-[#111]';
    return (
        <div className="bg-white rounded-xl border border-[#F3F4F6] p-3" data-testid={testId || `acc-comp-metric-${accent}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-bold mb-1">{label}</p>
            <p className={`text-base font-bold ${accentCls}`}>{negative && value > 0 ? '-' : ''}{fmt(value)} EUR</p>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">{typeof count === 'number' ? `${count || 0} kayit` : count === 'fee' ? 'kabul edilen' : '0 kayit'}</p>
        </div>
    );
}
